const $ = require('jquery');

/**
 * Represents the Wishlist Icon functionality.
 *
 * @class WishListIcon
 */
class WishListIcon {
    /**
     * Creates an instance of the WishListIcon class.
     *
     * @param {string} wishlistContainerSelector Selector
     * @class
     */
    constructor(wishlistContainerSelector) {
        this.$wishlistIconContainer = $(wishlistContainerSelector);
        if (!this.$wishlistIconContainer.length) {
            return;
        }

        this.isEnabledAddingToWishlist = Boolean(this.$wishlistIconContainer.data('is-enabled-adding'));
        this.addToWishlistUrl = this.$wishlistIconContainer.data('add-to-wishlist-url');
        this.removeFromWishlistUrl = this.$wishlistIconContainer.data('remove-from-wishlist-url');
        this.removeAriaLabel = this.$wishlistIconContainer.data('aria-label-remove');
        this.addAriaLabel = this.$wishlistIconContainer.data('aria-label-add');
        this.isOnlyForMembers = Boolean(this.$wishlistIconContainer.data('is-only-for-members'));

        const productId = String(this.$wishlistIconContainer.data('product-id'));
        this.updateWishlistButton(this.isInWishlist(productId), productId);
    }

    /**
     * Checks if the product with the given ID is in the wishlist.
     *
     * @function isInWishlist
     * @param {string} pid - The ID of the product to check.
     * @returns {boolean} - Indicates whether the product is in the wishlist.
     */
    isInWishlist(pid) {
        if (!this.isEnabledAddingToWishlist) {
            return false;
        }
        const $wishlistLink = $('a.mini-wishlist-link');
        const isProductInWishlist = String($wishlistLink.data('favpids')).split('|').includes(String(pid));

        return isProductInWishlist;
    }

    /**
     * Sets the wishlist button and icon with the provided attributes.
     *
     * @function setWishlistButtonAttributes
     * @param {Object} options - The options for updating the wishlist button.
     * @param {string} options.productId - The ID of the product.
     * @param {string} options.actionUrl - The URL for the wishlist action.
     * @param {string} options.ariaLabelAttr - The aria-label attribute for the wishlist button.
     * @param {string} options.heartIconClass - The CSS class for the heart icon.
     * @param {string} options.wishlistButtonClass - The CSS class for the wishlist button.
     * @param {string} options.stateClass - The CSS class for the wishlist state.
     * @returns {void}
     */
    setWishlistButtonAttributes({
        productId,
        actionUrl,
        ariaLabelAttr,
        heartIconClass,
        wishlistButtonClass,
        stateClass
    }) {
        const $wishlistButton = this.$wishlistIconContainer.find('button.product-wishlist-link');
        const $buttonIcon = $wishlistButton.find('i');

        $wishlistButton.addClass(wishlistButtonClass);
        $buttonIcon.addClass(heartIconClass);
        $('div.product-wishlist').addClass(stateClass);

        $wishlistButton.attr('data-href', actionUrl);
        $wishlistButton.attr('data-pid', productId);
        $wishlistButton.attr('aria-label', ariaLabelAttr);

        if (!this.isEnabledAddingToWishlist) {
            $wishlistButton.addClass('disabled');
        }
    }

    /**
     * Composes the attributes and classes for a wishlist button based on the product's wishlist status.
     *
     * @function composeWishlistButtonAttributes
     * @param {boolean} isProductInWishlist - Indicates whether the product is already in the wishlist.
     * @returns {Object} - The composed attributes and classes for the wishlist button.
     */
    composeWishlistButtonAttributes(isProductInWishlist) {
        const ariaLabelAttr = isProductInWishlist ? this.removeAriaLabel : this.addAriaLabel;
        const heartIconClass = isProductInWishlist ? 'icon-wishlist-active' : 'icon-wishlist';
        const wishlistButtonClass = isProductInWishlist ? 'in-wishlist' : 'not-in-wishlist';
        const stateClass = this.isOnlyForMembers ? 'members-only' : '';
        const actionUrl = isProductInWishlist ? this.removeFromWishlistUrl : this.addToWishlistUrl;

        return {
            actionUrl,
            ariaLabelAttr,
            heartIconClass,
            wishlistButtonClass,
            stateClass
        };
    }

    /**
     * Updates the wishlist button with the attributes based on given state and product ID.
     *
     * @function updateWishlistButton
     * @param {boolean} state - The state of the wishlist button (true for active, false for inactive).
     * @param {string} productId - The ID of the product associated with the wishlist button.
     */
    updateWishlistButton(state, productId) {
        const wishlistButtonAttributes = {
            state,
            productId,
            ...this.composeWishlistButtonAttributes(state)
        };

        this.setWishlistButtonAttributes(wishlistButtonAttributes);
    }
}

module.exports = WishListIcon;
